// OPERATOR-CONSOLE: Operator › Settings (`/operator/settings?section=&company=&satellite=`): every
// setting once set by hand, as versioned rows a platform admin edits, in twelve sections — a list
// on the left on wide screens, a select on narrow ones (no sideways scrolling either way). The
// company and satellite in the URL are the scope every card reads at.
import { ApiError } from "@companyos/sdk";
import { cn, EmptyState, LocationHeader, NativeSelect, NativeSelectOption } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontalIcon } from "lucide-react";
import { type ReactNode, useMemo } from "react";
import type { Api } from "../../../api/client";
import type { MeResponse } from "../../../api/types";
import { problemText } from "../../ops-parts";
import { OpsBody, OpsFrame, operatorCrumb } from "../../ops-ux";
import { LoadError, Loading } from "../../screen-parts";
import { EffortDefaultsCard } from "../effort-defaults-card";
import { satellitesQuery } from "../satellites-page";
import { OperatorAgents } from "./operator-agents";
import {
  BaselineSection,
  BasePromptSection,
  PricesSection,
  RosterSection,
  SatellitesSection,
} from "./sections";
import { ScopeContext, SettingCard, type SettingsScope } from "./setting-card";

export const SETTINGS_SECTIONS = [
  "prices",
  "models",
  "runtime",
  "baseline",
  "wake",
  "roster",
  "prompt",
  "skills",
  "rollout",
  "agents",
  "satellites",
  "limits",
] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

const LABELS: Record<SettingsSection, string> = {
  prices: "Prices",
  models: "Models",
  runtime: "Runtime profiles",
  baseline: "Baseline",
  wake: "Wake",
  roster: "Roster",
  prompt: "Base prompt",
  skills: "Skills library",
  rollout: "Rollout",
  agents: "Operator agents",
  satellites: "Satellites",
  limits: "Limits",
};

export function OperatorSettingsPage({
  api,
  me,
  section,
  company,
  satellite,
  onChange,
}: {
  api: Api;
  me: MeResponse;
  section: SettingsSection;
  company: string | null;
  satellite: string | null;
  onChange: (next: {
    section: SettingsSection;
    company: string | null;
    satellite: string | null;
  }) => void;
}) {
  // Who may: the list answers 403 to an agency user who is not a platform admin.
  const gate = useQuery({
    queryKey: ["operator", "settings", "list"],
    queryFn: () => api.client.operator.settings.list(),
  });
  const sats = useQuery({ ...satellitesQuery(api), enabled: gate.isSuccess });
  const companies = useMemo(
    () =>
      [...(me.agency?.grants ?? [])]
        .sort((a, b) => a.company_name.localeCompare(b.company_name))
        .map((g) => ({ id: g.company_id, name: g.company_name })),
    [me],
  );
  const satellites = useMemo(
    () =>
      (sats.data?.data ?? []).filter((s) => !s.retired_at).map((s) => ({ id: s.id, name: s.name })),
    [sats.data],
  );
  const scope: SettingsScope = {
    company,
    satellite,
    companies,
    satellites,
    setScope: (next) => onChange({ section, ...next }),
  };
  const forbidden = gate.error instanceof ApiError && gate.error.status === 403;
  const go = (s: SettingsSection) => onChange({ section: s, company, satellite });
  return (
    <OpsFrame data-slot="operator-settings">
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Settings" }]}
        icon={SlidersHorizontalIcon}
        title="Settings"
      />
      <OpsBody>
        {gate.isPending ? (
          <Loading label="Loading settings" />
        ) : forbidden ? (
          <EmptyState
            variant="card"
            icon={SlidersHorizontalIcon}
            title="Platform admins only"
            description={problemText(gate.error, "Settings are managed by a platform admin.")}
          />
        ) : gate.isError ? (
          <LoadError what="Settings" error={gate.error} />
        ) : (
          <ScopeContext.Provider value={scope}>
            <div className="grid min-w-0 gap-4 lg:grid-cols-[11rem_minmax(0,1fr)] lg:gap-6">
              <nav
                aria-label="Settings sections"
                className="max-lg:hidden"
                data-slot="settings-nav"
              >
                <ul className="sticky top-4 grid gap-0.5">
                  {SETTINGS_SECTIONS.map((s) => (
                    <li key={s}>
                      <button
                        type="button"
                        aria-current={s === section ? "page" : undefined}
                        data-section={s}
                        onClick={() => go(s)}
                        className={cn(
                          "w-full rounded-md px-2.5 py-1.5 text-left text-[13px] text-muted-foreground hover:bg-muted hover:text-foreground",
                          s === section && "bg-muted font-medium text-foreground",
                        )}
                      >
                        {LABELS[s]}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
              <NativeSelect
                aria-label="Section"
                className="w-full lg:hidden"
                value={section}
                onChange={(e) => go(e.target.value as SettingsSection)}
              >
                {SETTINGS_SECTIONS.map((s) => (
                  <NativeSelectOption key={s} value={s}>
                    {LABELS[s]}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <div className="flex min-w-0 flex-col gap-4" data-slot={`section-${section}`}>
                <h2 className="sr-only">{LABELS[section]}</h2>
                <Section api={api} section={section} />
              </div>
            </div>
          </ScopeContext.Provider>
        )}
      </OpsBody>
    </OpsFrame>
  );
}

function Stack({ children }: { children: ReactNode }) {
  return <div className="flex min-w-0 flex-col gap-4">{children}</div>;
}

function Section({ api, section }: { api: Api; section: SettingsSection }) {
  switch (section) {
    case "prices":
      return <PricesSection api={api} />;
    case "models":
      return (
        <Stack>
          <EffortDefaultsCard api={api} />
          <SettingCard api={api} settingKey="models" />
        </Stack>
      );
    case "runtime":
      return (
        <Stack>
          <SettingCard api={api} settingKey="runtime_profiles" />
          <SettingCard api={api} settingKey="container_memory" />
        </Stack>
      );
    case "baseline":
      return <BaselineSection api={api} />;
    case "wake":
      return (
        <Stack>
          <SettingCard api={api} settingKey="bridge_runtime" />
          <SettingCard api={api} settingKey="satellite_runtime" />
        </Stack>
      );
    case "roster":
      return <RosterSection api={api} />;
    case "prompt":
      return <BasePromptSection api={api} />;
    case "skills":
      return <SettingCard api={api} settingKey="skills_defaults" />;
    case "rollout":
      return <SettingCard api={api} settingKey="rollout" />;
    case "agents":
      return <OperatorAgents api={api} />;
    case "satellites":
      return <SatellitesSection api={api} />;
    case "limits":
      return (
        <Stack>
          <SettingCard api={api} settingKey="limits" />
          <SettingCard api={api} settingKey="de_defaults" />
          <SettingCard api={api} settingKey="nightly_review" />
        </Stack>
      );
  }
}
